import { Button } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { FC, ReactElement, ReactNode, useEffect } from "react";
import useUserAuthStage from "src/state/hooks/useUserAuthStage";
import AppAccessCubit from "src/state/AppAccessCubit/AppAccessCubit";
import { useBloc } from "src/state/state";
import LoginOverlay from "src/ui/components/LoginOverlay/LoginOverlay";
import Translate from "src/ui/components/Translate/Translate";
import Loader from "../Loader/Loader";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";
import { useNavigate } from "react-router-dom";
import UserCubit from "src/state/UserCubit/UserCubit";
import ReAuthenticateUsingTranscarent from "components/LoginForm/ReAuthenticateUsingTranscarent";
import ReAuthenticateUsingPersonify from "components/LoginForm/ReAuthenticateUsingPersonify";
import { useBlocNext } from "@blac/react";
import { View, WithAuthCubit } from "components/WithAuth/WithAuthCubit";
import AppAutoTriggers from "../AppAutoTriggers/AppAutoTriggers";
import LockedScreen from "components/LockedScreen/LockedScreen";
import { BiometricVerificationBloc } from "src/hybrid/components/BiometricVerification";
import { KnownPartnerSources } from "src/constants/partner";
import { getPartnerSourceByPayerId } from "src/constants/partnerConfig";
import AuthenticationBloc from "src/state/UserCubit/AuthenticationBloc";
import useBootScreenHold from "src/ui/hooks/useBootScreenHold";

const ErrorLayout = styled.div`
  label: WithAuthErrorLayout;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  min-height: 100vh;
  min-height: 100svh;
  text-align: center;
  padding: var(--space-lg);
`;

export interface Props {
  redirectIfNoToken?: boolean;
  loadProfile?: boolean;
  createAnonymousUser?: boolean;
  children?: ReactNode | undefined;
  allowPartialAuth?: boolean;
}

const WithAuth: FC<Props> = (props): ReactElement => {
  const navigate = useNavigate();
  const {
    authStage,
    authenticationStatus,
    credentialsRestored,
    sessionRestoreSettled,
    hasStoredCredentials
  } = useUserAuthStage();
  const [{ allSubscriptions }] = useBloc(SubscriptionCubit);

  const [{ userData }, { partnerId }] = useBloc(UserCubit);

  const [
    { isBiometricVerificationEnabled, isSessionVerified, requestedEmailLogin }
  ] = useBloc(BiometricVerificationBloc);

  // AppAccessCubit is a blac-next cubit, so it must go through useBlocNext.
  // Destructuring the status state key (not just a getter) is required for
  // blac-next to re-render this consumer when the cubit emits.
  const [{ status: appAccessStatus }] = useBlocNext(AppAccessCubit);

  const [{ view }, { initializeDependencies, retry }] =
    useBlocNext(WithAuthCubit);

  const [, { updateAuthStage }] = useBlocNext(AuthenticationBloc);

  // The boot screen from index.html stands in for this view's own loader while
  // the app starts up, so the two don't cross-fade over each other.
  useBootScreenHold(view === View.Loading);

  // Single owner of the auth stage refresh — useUserAuthStage is read-only so
  // its other consumers do not each trigger their own profile fetch.
  useEffect(() => {
    void updateAuthStage();
  }, [
    userData?.passwordSet,
    userData?.email,
    userData?.sessionAuthenticationMethod
  ]);

  // Sync all auth-related state to WithAuthCubit which determines the current view
  useEffect(() => {
    initializeDependencies({
      props,
      navigate,
      allSubscriptions,
      payerId: partnerId,
      sessionAuthenticationMethod: userData?.sessionAuthenticationMethod,
      authStage,
      authenticationStatus,
      credentialsRestored,
      sessionRestoreSettled,
      hasStoredCredentials,
      isBiometricEnabled: isBiometricVerificationEnabled,
      isBiometricVerified: isSessionVerified,
      requestedEmailLogin,
      appAccessStatus
    });
  }, [
    props,
    navigate,
    allSubscriptions,
    partnerId,
    userData,
    authStage,
    authenticationStatus,
    credentialsRestored,
    sessionRestoreSettled,
    hasStoredCredentials,
    isBiometricVerificationEnabled,
    isSessionVerified,
    requestedEmailLogin,
    appAccessStatus,
    initializeDependencies
  ]);

  if (view === View.App) {
    return (
      <>
        {props.children}
        <AppAutoTriggers />
      </>
    );
  }

  if (view === View.Locked) {
    return <LockedScreen />;
  }

  if (view === View.Loading) {
    return <Loader gradient active fixed bg="branded" />;
  }

  if (view === View.Reauth) {
    const partnerSource = getPartnerSourceByPayerId(partnerId);
    if (partnerSource === KnownPartnerSources.personifyHealth) {
      return <ReAuthenticateUsingPersonify />;
    }
    return <ReAuthenticateUsingTranscarent />;
  }

  if (view === View.Error) {
    return (
      <ErrorLayout>
        <h3 className="as-h4-large">
          <Translate msg="error_generic_title" />
        </h3>
        <p className="m0 color-c-80">
          <Translate msg="error.generic.short.tryAgain" />
        </p>
        <Button theme="charcoal" center onPress={retry}>
          <Translate msg="retry" />
        </Button>
      </ErrorLayout>
    );
  }

  return <LoginOverlay />;
};

export default WithAuth;
